import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, input, ElementRef, viewChild, inject, effect, untracked, OnDestroy } from '@angular/core';
import { extend, injectStore, NgtArgs } from 'angular-three';
import { ArService } from '../../services/ar.service';
import { AudioService, ConfettiService } from '@kanek/shared-ui';
import { NgtsHTML, NgtsHTMLContent, NgtsHTMLOptions } from 'angular-three-soba/misc';
import { NgtsOrbitControls } from 'angular-three-soba/controls';
import { UiDiceRollerComponent, DiceRollerConfig, DiceRollResult } from '@kanek/shared/ui-dice-roller';
import { calculateHTMLDimensions3D } from '../../utils/three-scene.utils';
import { EMPIRICAL_CORRECTIONS } from '../../constants/three-scene.constants';
import * as THREE from 'three';
import {
  BoxGeometry,
  ConeGeometry,
  PlaneGeometry,
  MeshStandardMaterial,
  ShadowMaterial,
  AmbientLight,
  DirectionalLight,
  HemisphereLight,
  Mesh,
  Vector3,
  Euler
} from 'three';
import { DiceRollSettings, DiceRollLegendSlot, DICE_ROLL_PIXELS_PER_UNIT, DICE_ROLL_SURFACE_SIZE_UNITS, DICE_ROLL_PHYSICS, DICE_ROLL_SURFACE_SIZE_PIXELS } from './dice-roll.types';
import { DiceRollRenderer } from './dice-roll-renderer';

// Extend Three.js objects for Angular Three
extend({
  BoxGeometry,
  ConeGeometry,
  PlaneGeometry,
  MeshStandardMaterial,
  ShadowMaterial,
  AmbientLight,
  DirectionalLight,
  HemisphereLight,
  Mesh,
  Vector3,
  Euler
});

/**
 * Dice Roll Scene Component
 *
 * Declarative Angular Three component that renders the dice roll game in 3D:
 * - Wood table surface (box geometry)
 * - HTML legend overlay (projected onto table top)
 * - 2 dice (Cannon.js physics)
 *
 * Architecture:
 * - Pure presentation component (stateless)
 * - All state passed via scene.userData or @Input()
 * - Reactive geometry using computed signals
 * - Follows Angular Three patterns
 */
@Component({
  selector: 'lib-dice-roll-scene',
  standalone: true,
  imports: [
    NgtArgs,
    NgtsHTML,
    NgtsHTMLContent,
    NgtsOrbitControls,
    UiDiceRollerComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <!-- Three-Point Lighting System -->

    <!-- Ambient: Base illumination -->
    <ngt-ambient-light [intensity]="0.8" />

    <!-- Hemisphere: Natural sky/ground gradient lighting -->
    <ngt-hemisphere-light
      [skyColor]="16777215"
      [groundColor]="4473924"
      [intensity]="0.5"
    />

    <!-- Key Light: Main directional (strongest, casts shadows) -->
    <ngt-directional-light
      [position]="[5, 8, 5]"
      [intensity]="2.5"
      [castShadow]="true"
    />

    <!-- Fill Light: Softer secondary (reduces harsh shadows) -->
    <ngt-directional-light
      [position]="[-3, 4, -2]"
      [intensity]="0.8"
    />

    <!-- Rim Light: Back-highlight (adds depth and separation) -->
    <ngt-directional-light
      [position]="[0, 3, -6]"
      [intensity]="1.0"
    />

    <!-- OrbitControls (conditional on enableCameraControls) -->
    @if (enableCameraControls()) {
      <ngts-orbit-controls
        [options]="{
          enableDamping: true,
          dampingFactor: 0.05,
          enablePan: true
        }" />
    }

    <!-- Scene Group (rotation depends on surfaceOrientation) -->
    <ngt-group [rotation]="sceneGroupRotation()">
      <!-- Table Surface (wood box) -->
      <ngt-mesh
        [position]="[0, 0, 0]"
        [castShadow]="settingsData()?.castShadow ?? true"
        [receiveShadow]="true">
        <ngt-value [rawValue]="surfaceGeometry()" attach="geometry" />
        <ngt-value [rawValue]="surfaceMaterial()" attach="material" />
      </ngt-mesh>

      <!-- Dice Roller Component (2 dice) -->
      <lib-dice-roller
        [config]="diceConfig()"
        (rollComplete)="onRollComplete($event)"
      />

      <!-- Reflective Overlay Mesh (reflections) -->
      <ngt-mesh
        [position]="htmlReflectiveOverlayPosition()"
        [scale]="htmlReflectiveOverlayScale()"
        [receiveShadow]="true"
        [castShadow]="false">
        <ngt-plane-geometry *args="[1, 1]" />
        <ngt-mesh-standard-material
          [transparent]="true"
          [opacity]="0.1"
          [roughness]="0.0"
          [metalness]="1.0"
          [side]="FrontSide"
          [depthWrite]="false"
          [blending]="Blending"]
        />
      </ngt-mesh>

      <!-- Shadow Overlay Mesh -->
      <ngt-mesh
        [position]="htmlReflectiveOverlayPosition()"
        [scale]="htmlReflectiveOverlayScale()"
        [receiveShadow]="true"
        [castShadow]="false">
        <ngt-plane-geometry *args="[1, 1]" />
        <ngt-shadow-material
        [transparent]="true"
          [opacity]="0.1"
          [side]="FrontSide"
          [depthWrite]="false"
        />
      </ngt-mesh>

      <!-- HTML Legend Overlay (LAST child for proper occlusion) -->
      <ngts-html [options]="htmlOverlayOptions()">
        <!-- Occlusion geometry: auto-sized by ngts-html to match HTML content -->
        <ngt-plane-geometry
          data-occlusion-geometry
        />
        <!-- Custom occlusion material to avoid shader compilation errors -->
        <ngt-mesh-basic-material
          data-occlusion-material
          [colorWrite]="false"
        />
        <div [ngtsHTMLContent]="htmlContentOptions()">
          <div
            #legendHost
            class="dice-roll-host"
            [style.width]="surfaceDimensions().width"
            [style.height]="surfaceDimensions().height">
          </div>
        </div>
      </ngts-html>
    </ngt-group>
  `,
  styles: [`
    :host {
      display: contents;
    }
  `]
})
export class DiceRollSceneComponent implements OnDestroy {
  // Inputs for direct usage
  settings = input<DiceRollSettings | null>(null);
  boardContainer = input<HTMLElement | null>(null);

  // ViewChild to get the rendered legend host element
  private legendHost = viewChild<ElementRef<HTMLDivElement>>('legendHost');

  // ViewChild to get OrbitControls instance (when enabled)
  private orbitControlsRef = viewChild(NgtsOrbitControls);

  private store = injectStore();
  protected arService = inject(ArService);
  private audioService = inject(AudioService);
  private confettiService = inject(ConfettiService);

  // Store renderer instance to preserve game state across settings changes
  private legendRenderer: DiceRollRenderer | null = null;

  // Constants for template
  protected readonly Math = Math;
  protected readonly FrontSide = THREE.FrontSide;
  protected readonly Blending = THREE.AdditiveBlending;

  // Computed: Settings from scene.userData or input
  protected settingsData = computed(() => {
    const inputSettings = this.settings();

    // Read scene via untracked to avoid subscribing to 60fps NgtStore clock updates.
    // The configSignal itself is still called in tracked context so reactive config changes work.
    const scene = untracked(() => this.store.get()).scene;
    const configSignal = scene?.userData?.['configSignal'];
    const config = configSignal ? configSignal() : null;
    const sceneSettings = config?.['settings'];

    return inputSettings || sceneSettings || null;
  });

  // Computed: Items from scene.userData
  protected itemsData = computed(() => {
    // Read scene via untracked to avoid frame-reactive subscription.
    const scene = untracked(() => this.store.get()).scene;
    const configSignal = scene?.userData?.['configSignal'];
    const config = configSignal ? configSignal() : null;
    return config?.['items'] || [];
  });

  // Computed: Base HTML dimensions converted to 3D units (includes 1% correction from shared utility)
  // store.get() is wrapped in untracked() so this computed does NOT re-run on every NgtStore
  // clock tick (which would recreate BoxGeometry/MeshStandardMaterial objects at 60Hz → GPU leak).
  protected htmlDimensions3D = computed<{ width: number; height: number }>(() => {
    const settings = this.settingsData();
    const surfaceSize: 'small' | 'medium' | 'large' = settings?.surfaceSize || 'medium';
    const pixelsPerUnit = DICE_ROLL_PIXELS_PER_UNIT[surfaceSize];
    const surfacePixels = DICE_ROLL_SURFACE_SIZE_UNITS * pixelsPerUnit;

    return calculateHTMLDimensions3D(surfacePixels, surfacePixels, untracked(() => this.store.get()));
  });

  // Computed: Surface dimensions (box geometry uses width/height/depth, not radius)
  protected surfaceWidth = computed(() => {
    // Add 3% border margin on top of actual HTML dimensions (accounts for orientation)
    return this.actualHTMLDimensions3D().width * EMPIRICAL_CORRECTIONS.BOARD_BORDER_MARGIN;
  });

  protected surfaceHeight = computed(() => {
    // Add 3% border margin on top of actual HTML dimensions (accounts for orientation)
    return this.actualHTMLDimensions3D().height * EMPIRICAL_CORRECTIONS.BOARD_BORDER_MARGIN;
  });

  // Computed: Playable surface size (surfaceWidth without 3% margin)
  protected surfaceSize = computed(() => {
    return this.surfaceWidth() / EMPIRICAL_CORRECTIONS.BOARD_BORDER_MARGIN;
  });

  // Computed: Surface pixel dimensions for HTML container
  // Always horizontal orientation (2-column grid layout)
  protected surfaceDimensions = computed(() => {
    const settings = this.settingsData();
    const surfaceSize: 'small' | 'medium' | 'large' = settings?.surfaceSize || 'medium';
    
    // Calculate from units: totalUnits × pixelsPerUnit → surfacePixels
    const pixelsPerUnit = DICE_ROLL_PIXELS_PER_UNIT[surfaceSize];
    const surfacePixels = DICE_ROLL_SURFACE_SIZE_UNITS * pixelsPerUnit; // e.g., 20 × 50 = 1000px
    
    // Square container for horizontal 2-column grid
    return {
      width: `${surfacePixels}px`,
      height: `${surfacePixels}px`
    };
  });

  // Computed: Surface size scale factor (for scaling dice relative to surface)
  protected surfaceSizeScaleFactor = computed(() => {
    const settings = this.settingsData();
    const surfaceSize: 'small' | 'medium' | 'large' = settings?.surfaceSize || 'medium';
    // Scale factor relative to medium (1000px baseline)
    // Small: 750px → 0.75x, Medium: 1000px → 1.0x, Large: 1250px → 1.25x
    const scaleFactor = DICE_ROLL_SURFACE_SIZE_PIXELS[surfaceSize] / DICE_ROLL_SURFACE_SIZE_PIXELS['medium'];
    return scaleFactor;
  });

  // Computed: Surface depth (thickness of the table box)
  protected surfaceDepth = computed(() => {
    const settings = this.settingsData();
    return settings?.surfaceDepth ?? 0.2; // Thicker box for wood table (0.2)
  });

  // Computed: Surface geometry (wood table top)
  protected surfaceGeometry = computed(() => {
    return new BoxGeometry(
      this.surfaceWidth(),
      this.surfaceHeight(),
      this.surfaceDepth()
    );
  });

  protected surfaceMaterial = computed(() => {
    const settings = this.settingsData();
    return new MeshStandardMaterial({
      color: settings?.surfaceColor ?? '#654321', // Rich brown wood
      roughness: settings?.surfaceRoughness ?? 0.9, // Matte wood finish
      metalness: settings?.surfaceMetalness ?? 0.1, // Wood has minimal metallic properties
      envMapIntensity: 0.3 // Subtle environmental reflections
    });
  });

  // Computed: Dice configuration for dice-roller component
  protected diceConfig = computed<DiceRollerConfig>(() => {
    const settings = this.settingsData();
    const surfaceOrientation = settings?.surfaceOrientation || 'horizontal';
    const bypassActive = this.arService.shouldBypassAR;
    const surfaceDepth = this.surfaceDepth();

    // Floor is at the top surface of the table box
    const floorZ = surfaceDepth / 2 - 0.09;

    // Get scale factor for all scaling calculations
    const scaleFactor = this.surfaceSizeScaleFactor();

    // Start dice above the floor - scale the offset with surface size
    const startZ = floorZ + (DICE_ROLL_PHYSICS.DIE_START_HEIGHT_MULTIPLIER * scaleFactor);

    // Scene rotation for dice-roller (matches scene group rotation)
    let sceneRotation: [number, number, number] = [0, 0, 0];
    if (!bypassActive && surfaceOrientation === 'vertical') {
      sceneRotation = [-Math.PI / 2, 0, 0];
    }

    // Perfect scaling approach:
    // - Dice created at size 1.0, then scaled via mesh.scale (maintains geometry proportions)
    // - Mass scales by S³ (volume) to maintain constant density
    // - Impulse scales by S⁴ to maintain consistent angular velocity
    // - Gravity constant ensures proper proportional scaling
    // Result: Physics feels identical across small (750px), medium (1000px), large (1250px)

    // Tutorial params (constant for size=1.0 die)
    return {
      diceCount: 2, // Two dice for gameplay
      mode: 'predetermined',
      size: 1.0, // Always create at base size
      edgeRadius: 0.07,
      segments: 50,
      notchDepth: 0.1,
      notchRadius: 0.12,
      scale: scaleFactor, // Scale the entire mesh (0.75/1.0/1.25)
      gravity: [0, 0, -DICE_ROLL_PHYSICS.BASE_GRAVITY], // Constant gravity for perfect scaling
      floorPosition: [0, 0, floorZ], // Floor at surface top
      startPosition: [0, 0, startZ], // Start above floor (Z-axis is height)
      sceneRotation
    };
  });

  // Computed: Pawn geometry and material (fallback for when GLB not loaded)
  protected pawnGeometry = computed(() => {
    return new ConeGeometry(0.3, 0.8, 8);
  });

  protected pawnMaterial = computed(() => {
    const settings = this.settingsData();
    return new MeshStandardMaterial({
      color: settings?.pawnColor ?? '#4CAF50', // Configurable pawn color
      roughness: 0.4, // Slightly glossy painted finish
      metalness: 0.3, // Subtle metallic sheen
      envMapIntensity: 0.5 // Enhanced reflections for painted surface
    });
  });

  // Computed: Legend slots from scene.userData
  protected legendSlots = computed(() => {
    const scene = untracked(() => this.store.get()).scene;
    return (scene?.userData?.['legendSlots'] as DiceRollLegendSlot[]) || [];
  });

  // Computed: Scene group rotation (depends on orientationMode + AR state)
  protected sceneGroupRotation = computed<[number, number, number]>(
    () => {
      const settings = this.settingsData();
      const surfaceOrientation = settings?.surfaceOrientation || 'horizontal';
      const enableAR = untracked(() => this.store.get()).scene?.userData?.['enableAR'] ?? false;

      // Orientation setting only applies in AR mode
      // In non-AR mode (orbit controls), always show bird's eye view (flat on ground)
      if (!enableAR) {
        return [0, 0, 0]; // Bird's eye view for orbit controls
      }

      // In AR mode, apply rotation based on surfaceOrientation
      if (surfaceOrientation === 'horizontal') {
        // Horizontal: Surface parallel to marker (lying flat on table)
        return [-Math.PI / 2, 0, 0];
      } else {
        // Vertical: Surface perpendicular to marker (standing up, facing user)
        // Rotate 180° around Y-axis to face front side toward camera
        return [Math.PI, 0, 0];
      }
    },
    { equal: (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2] }
  );

  // Computed: Enable camera controls
  protected enableCameraControls = computed(() => {
    const settings = this.settingsData();
    const fromSettings = settings?.enableCameraControls ?? false;
    const fromUserData = untracked(() => this.store.get()).scene?.userData?.['enableCameraControls'] ?? false;
    const bypassActive = this.arService.shouldBypassAR;
    return bypassActive || fromSettings || fromUserData;
  });

  // Computed: HTML overlay options
  protected htmlOverlayOptions = computed((): Partial<NgtsHTMLOptions> => {
    const surfaceDepth = this.surfaceDepth();
    // Z offset: Position HTML just in front of box face (like scratchcard)
    const htmlZOffset = surfaceDepth / 2 + 0.01;

    // Track surface dimensions to trigger re-evaluation when size changes
    this.surfaceDimensions();

    return {
      occlude: 'blending',  // Use blending mode for proper depth sorting with dice
      transform: true,
      scale: 1.0,  // Fixed scale (use preset surface sizes instead)
      // Position centered on top of the box
      position: [0, 0, htmlZOffset] as [number, number, number],
      // No rotation - HTML sits flat on box by default
      rotation: [0, 0, 0] as [number, number, number]
    };
  });

  // Computed: HTML content options
  protected htmlContentOptions = computed(() => {
    return {
      distanceFactor: 0,  // HTML at pixel size
      zIndexRange: [100, 1] as [number, number]
    };
  });

  // Computed: Reflective overlay position (slightly in front of HTML)
  protected htmlReflectiveOverlayPosition = computed<[number, number, number]>(() => {
    const surfaceDepth = this.surfaceDepth();
    const htmlZOffset = surfaceDepth / 2 + 0.01;
    // Position overlay 0.01 units in front of HTML
    return [0, 0, htmlZOffset + 0.01];
  });

  // Computed: Actual HTML container dimensions in 3D units
  // Always horizontal orientation (square surface)
  protected actualHTMLDimensions3D = computed<{ width: number; height: number }>(() => {
    const settings = this.settingsData();
    const surfaceSize: 'small' | 'medium' | 'large' = settings?.surfaceSize || 'medium';
    const pixelsPerUnit = DICE_ROLL_PIXELS_PER_UNIT[surfaceSize];
    const surfacePixels = DICE_ROLL_SURFACE_SIZE_UNITS * pixelsPerUnit;
    
    // Horizontal: square container for 2-column grid
    return calculateHTMLDimensions3D(surfacePixels, surfacePixels, untracked(() => this.store.get()));
  });

  // Computed: Reflective overlay scale (pixel-perfect match to HTML content)
  protected htmlReflectiveOverlayScale = computed<[number, number, number]>(() => {
    // Use actual HTML dimensions that account for legend orientation
    const dimensions = this.actualHTMLDimensions3D();
    return [dimensions.width, dimensions.height, 1];
  });

  /**
   * Create a canvas texture with a number for die face
   * @deprecated This method is no longer used - dice-roller library uses procedural geometry
   */
  private createDieFaceMaterial(_number: number): MeshStandardMaterial {
    return new MeshStandardMaterial({ color: 0xffffff });
  }

  ngOnDestroy(): void {
    // Dispose Three.js GPU resources to prevent memory leaks
    this.surfaceGeometry()?.dispose();
    this.pawnGeometry()?.dispose();
    (this.surfaceMaterial() as MeshStandardMaterial | null)?.dispose();
    (this.pawnMaterial() as MeshStandardMaterial | null)?.dispose();
  }

  /**
   * Roll 2 dice to achieve a target total using dice-roller library.
   * The simulation will accept ANY combination that totals to the target.
   * Returns the actual dice values from the simulation.
   * 
   * @param targetTotal - Target sum of both dice (2-12)
   * @returns Promise resolving to actual dice values from simulation
   */
  async rollDice(targetTotal: number): Promise<{ values: number[] } | null> {
    const scene = untracked(() => this.store.get()).scene;
    const diceRoller = scene?.userData?.['diceRoller'];

    if (!diceRoller) {
      console.error('[DiceRollScene] Dice roller not found in scene.userData');
      return null;
    }

    try {
      // Roll two dice for target total (any combination accepted)
      const result = await diceRoller.roll(targetTotal); // Pass target total directly
      
      // Return actual values from simulation for verification
      return { values: result.values };
    } catch (error) {
      console.error('[DiceRollScene] Dice roll failed:', error);
      return null;
    }
  }

  /**
   * Reset dice state. Call when restarting game.
   */
  resetDice(): void {
    const scene = untracked(() => this.store.get()).scene;
    const diceRoller = scene?.userData?.['diceRoller'];

    if (diceRoller && typeof diceRoller.reset === 'function') {
      diceRoller.reset();
    }
  }

  /**
   * Reset the entire game - resets game state
   * Called by refresh button in Experience Studio
   */
  resetGame(): void {
    console.log('[DiceRollScene] Resetting game');
    this.resetDice();

    // Re-enable the CTA button via GameEngine's showCTA signal
    const scene = untracked(() => this.store.get()).scene;
    const showCTA = scene?.userData?.['showCTA'];
    if (showCTA) {
      showCTA.set(true);
    }

    // Dispatch reset event for renderer to handle
    if (scene) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (scene as any).dispatchEvent({
        type: 'game-reset'
      });
    }
  }

  /**
   * Handle roll complete event from dice-roller component
   */
  protected onRollComplete(_result: DiceRollResult): void {
    // Roll complete event handled
  }

  constructor() {
    // Effect: Initialize legend when legendHost is ready and we have config
    effect(() => {
      const host = this.legendHost();
      const settings = this.settingsData();
      const items = this.itemsData();

      if (host?.nativeElement && settings && items && items.length >= 6) {
        this.initializeLegend(host.nativeElement, settings, items);
      }
    });

    // Effect: Store OrbitControls reference in scene.userData when ready
    effect(() => {
      const scene = untracked(() => this.store.get()).scene;
      const controls = this.orbitControlsRef();
      if (scene && controls) {
        const orbitControls = controls.controls();
        scene.userData['orbitControls'] = orbitControls;
      }
    });

    // Effect: Expose animation methods and services to scene.userData for renderer.
    // NOTE: store.get() is tracked so this effect re-runs when the scene becomes
    // available. Only cheap userData assignments — no Three.js object creation.
    effect(() => {
      const scene = this.store.get().scene;
      if (scene) {
        scene.userData = scene.userData || {};
        scene.userData['rollDice'] = (targetTotal: number) => this.rollDice(targetTotal);
        scene.userData['resetDice'] = () => this.resetDice();
        scene.userData['resetGame'] = () => this.resetGame();
        scene.userData['confettiService'] = this.confettiService;
        scene.userData['audioService'] = this.audioService;
      }
    });
  }

  /**
   * Initialize game legend directly in the legendHost div
   * Used when DiceRollScene is loaded via GameEngine sceneGraph
   */
  private async initializeLegend(
    container: HTMLElement,
    settings: DiceRollSettings,
    items: Array<{
      id?: string;
      name?: string;
      label?: string;
      probability?: number;
      value?: string;
      icon?: string;
      [key: string]: unknown
    }>
  ): Promise<void> {
    try {
      // Check if reset was requested (from refresh button)
      const resetRequested = (settings as any)._resetRequested;
      if (resetRequested && this.legendRenderer) {
        this.resetGame();

        // Clear the reset flag to prevent re-triggering
        const scene = this.store.get().scene;
        const configSignal = scene?.userData?.['configSignal'];
        if (configSignal) {
          const currentConfig = configSignal();
          const newSettings = { ...currentConfig.settings };
          delete (newSettings as any)._resetRequested;
          configSignal.set({ ...currentConfig, settings: newSettings });
        }
      }

      // Create renderer instance if it doesn't exist
      if (!this.legendRenderer) {
        this.legendRenderer = new DiceRollRenderer();
        this.legendRenderer.setArService(this.arService);
        // Initialize the renderer with the container (only once)
        this.legendRenderer.initialize(container, this.arService);
      }

      // Convert items to prize format
      const prizes = items.map((item, index) => ({
        id: item.id || `${index + 1}`,
        label: item.name || item.label || `Prize ${index + 1}`,
        value: item.value || item.name || item.label || `Prize ${index + 1}`,
        icon: item.icon,
        probability: item.probability ?? (1 / items.length)
      }));

      // Add _previewPrizes to settings for demo mode
      const configSettings = {
        ...settings,
        _previewPrizes: prizes
      } as any;

      // Update renderer config to generate legend HTML
      await this.legendRenderer.updateConfig({
        settings: configSettings,
        items: prizes
      });
    } catch (error) {
      console.error('[DiceRollScene] Failed to initialize legend:', error);
    }
  }
}
